import { useEffect, useState } from "react";
import { Collapse, IconButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ChevronDown, Mail, Send } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import {
  Box,
  Chip,
  CircularProgress,
  Modal,
  Stack,
  Tooltip,
  Typography,
} from "../ds";
import type { ChipColorVariant } from "../ds";
import {
  useEmailContent,
  useEmailDeliveries,
  useResendEmail,
} from "../hooks/useEmailDelivery";
import { useIsCustodian } from "../hooks/useIsAdmin";
import { formatDateTime, formatRelativeTime } from "../utils/format";
import { recipientSummary } from "../utils/recipientDisplay";
import type { EmailDelivery } from "../api/emails";

// Acronyms the humaniser must not title-case. Small, stable vocabulary —
// the event slugs come from the backend's notification hooks.
const ACRONYMS = new Set(["ess", "wc", "ssc", "sc", "hr", "hod", "hof"]);

/** "wc_evaluation_recorded" -> "WC Evaluation Recorded". */
export function humaniseEvent(event: string | null | undefined): string {
  return (event || "Notification")
    .replace(/[_.]+/g, " ")
    .replace(/\w+/g, (word) =>
      ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    );
}

/** Delivery status -> chip copy + tone. Anything the backend reports that
 *  isn't a settled outcome reads as still in flight. */
function statusChip(status: string): {
  label: string;
  colorVariant: ChipColorVariant;
} {
  const s = (status ?? "").trim().toLowerCase();
  if (s === "sent") return { label: "Sent", colorVariant: "success" };
  if (s === "failed") return { label: "Failed", colorVariant: "error" };
  return { label: "Pending", colorVariant: "warning" };
}

// Every link in the preview opens in a new tab rather than navigating the
// sandboxed frame, which would strand the reader on a blank dialog.
const PREVIEW_HEAD =
  '<base target="_blank" rel="noopener">' +
  // The preview renders someone else’s email inside a sandboxed iframe —
  // that document is not a themed app surface, so it keeps paper white.
  "<style>html,body{margin:0;background:#fff}" + // allow-hex
  "body{font-family:system-ui,sans-serif;overflow-wrap:break-word}" +
  "*{max-width:100%;box-sizing:border-box}img{height:auto}" +
  "table{width:100%}</style>";

function previewDocument(html: string): string {
  return html.includes("</head>")
    ? html.replace("</head>", `${PREVIEW_HEAD}</head>`)
    : PREVIEW_HEAD + html;
}

interface PreviewProps {
  email: EmailDelivery | null;
  onClose: () => void;
}

/** The stored body, rendered in a sandboxed frame. Scripts are blocked;
 *  the email is someone else's HTML and never runs inside the app. */
function EmailPreview({ email, onClose }: PreviewProps) {
  const { data, isLoading, isError } = useEmailContent(
    email?.id ?? null,
    !!email,
  );
  return (
    <Modal
      open={!!email}
      onClose={onClose}
      title={email?.subject ?? "Email"}
      maxWidth="md"
    >
      <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1.5 }}>
        {email ? humaniseEvent(email.event) : ""}
        {email ? ` · ${formatDateTime(email.created_at)}` : ""}
      </Typography>
      {isLoading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : isError || !data ? (
        <Typography sx={{ fontSize: 13, color: "error.main" }}>
          Couldn't load this email's content.
        </Typography>
      ) : (
        <Box
          component="iframe"
          title="Email preview"
          srcDoc={previewDocument(data.html)}
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          sx={{
            width: "100%",
            height: { xs: 360, sm: 520 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.paper",
          }}
        />
      )}
    </Modal>
  );
}

interface RowProps {
  email: EmailDelivery;
  canResend: boolean;
  resending: boolean;
  onOpen: (email: EmailDelivery) => void;
  onResend: (emailId: string) => void;
}

function EmailRow({ email, canResend, resending, onOpen, onResend }: RowProps) {
  const chip = statusChip(email.status);
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      role="button"
      tabIndex={0}
      aria-label={`Open email: ${email.subject}`}
      onClick={() => onOpen(email)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(email);
        }
      }}
      sx={{
        py: 0.75,
        px: 0.75,
        borderRadius: 1,
        cursor: "pointer",
        "&:hover": { bgcolor: "action.hover" },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {email.subject || humaniseEvent(email.event)}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            color: "text.secondary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {recipientSummary(email.recipients, email.recipient_details)}
          {" · "}
          {formatRelativeTime(email.created_at)}
        </Typography>
      </Box>
      <Chip
        label={chip.label}
        colorVariant={chip.colorVariant}
        chipVariant="outlined"
      />
      {canResend && (
        <Tooltip title="Resend email">
          <Box component="span" sx={{ display: "inline-flex" }}>
            <IconButton
              size="small"
              aria-label={`Resend: ${email.subject}`}
              disabled={resending}
              onClick={(e) => {
                e.stopPropagation();
                onResend(email.id);
              }}
            >
              {resending ? (
                <CircularProgress size={14} />
              ) : (
                <RavenIcon icon={Send} sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </Box>
        </Tooltip>
      )}
    </Stack>
  );
}

interface Props {
  suggestionId: string;
}

/**
 * Read-only delivery log for the notification emails one suggestion has
 * produced, in the right rail below the reviewer roster. Same collapsible
 * shape as `RailReviewers`. Opening a row shows the stored body; resending
 * is offered to admins and scheme custodians only — the backend is the
 * authority, this only keeps the affordance off everyone else's rail.
 */
export default function EmailDeliverySection({ suggestionId }: Props) {
  // Collapsed by default: the log is reference material under the status
  // history, and the header's count already says whether anything was sent.
  // Opening it is per visit — a new suggestion starts collapsed again.
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [suggestionId]);
  const [preview, setPreview] = useState<EmailDelivery | null>(null);
  const { data, isLoading, isError } = useEmailDeliveries(suggestionId);
  const { isCustodian } = useIsCustodian();
  const resend = useResendEmail(suggestionId);

  const emails = data ?? [];
  // Hold the section back until the first fetch settles, so the rail
  // doesn't flash an empty section on every page load.
  if (isLoading) return null;

  return (
    <Box sx={{ pt: 2, mt: 2, borderTop: "1px solid", borderColor: "divider" }}>
      {/* The tour anchors on the header: the section opens collapsed, so
          the header is what the step can point at. */}
      <Stack
        data-tour="detail-emails"
        direction="row"
        alignItems="center"
        spacing={1.25}
        onClick={() => setOpen((o) => !o)}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        sx={{
          cursor: "pointer",
          userSelect: "none",
          borderRadius: 1,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Box
          sx={(theme) => ({
            width: 28,
            height: 28,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "primary.main",
          })}
        >
          <RavenIcon icon={Mail} sx={{ fontSize: 16 }} />
        </Box>
        <Typography
          sx={{ fontSize: 16, fontWeight: 600, color: "primary.main", flex: 1 }}
        >
          Email Notifications
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          ({emails.length})
        </Typography>
        <Box
          component="span"
          sx={{ color: "text.secondary", display: "inline-flex" }}
        >
          <RavenIcon
            icon={ChevronDown}
            style={{
              transition: "transform .15s ease",
              transform: open ? "rotate(0deg)" : "rotate(-90deg)",
            }}
          />
        </Box>
      </Stack>

      <Collapse in={open}>
        <Stack spacing={0.25} sx={{ mt: 1 }}>
          {isError ? (
            <Typography sx={{ fontSize: 12, color: "error.main", px: 0.75 }}>
              Couldn't load the notification log.
            </Typography>
          ) : emails.length === 0 ? (
            <Typography
              sx={{ fontSize: 12, color: "text.secondary", px: 0.75 }}
            >
              No notifications sent yet.
            </Typography>
          ) : (
            emails.map((email) => (
              <EmailRow
                key={email.id}
                email={email}
                canResend={isCustodian}
                resending={
                  resend.isPending && resend.variables === email.id
                }
                onOpen={setPreview}
                onResend={resend.mutate}
              />
            ))
          )}
        </Stack>
      </Collapse>

      <EmailPreview email={preview} onClose={() => setPreview(null)} />
    </Box>
  );
}
